import { LayoutGrid, List } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';

export type ListView = 'list' | 'cards';

/** A List / Cards choice remembered in this browser under `key`. */
export const useStoredListView = (key: string): [ListView, (view: ListView) => void] => {
  const [view, setView] = useState<ListView>(() => {
    try {
      return localStorage.getItem(`hr-view:${key}`) === 'cards' ? 'cards' : 'list';
    } catch {
      return 'list';
    }
  });
  const choose = (next: ListView) => {
    setView(next);
    try {
      localStorage.setItem(`hr-view:${key}`, next);
    } catch {
      // Storage blocked: the choice lasts until reload.
    }
  };
  return [view, choose];
};

interface ViewSwitchProps {
  value: ListView;
  onChange: (view: ListView) => void;
  className?: string;
}

/** The List / Cards switch of a directory (prototype `viewSeg`): two icon toggles. */
export const ViewSwitch = ({ value, onChange, className }: ViewSwitchProps) => {
  const { t } = useTranslation();
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      size="icon"
      value={value}
      onValueChange={(next) => next && onChange(next as ListView)}
      aria-label={t('VIEW')}
      className={className}
    >
      <ToggleGroupItem value="list" aria-label={t('LIST_VIEW')} title={t('LIST_VIEW')}>
        <List />
      </ToggleGroupItem>
      <ToggleGroupItem value="cards" aria-label={t('CARDS_VIEW')} title={t('CARDS_VIEW')}>
        <LayoutGrid />
      </ToggleGroupItem>
    </ToggleGroup>
  );
};
